import type * as React from "react";
import { useState } from "react";

// Third Party
import { useQueryClient, useMutation } from "@tanstack/react-query";

// UI
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FileUpload } from "@/components/FileUpload";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Icons
import { CirclePlus, ArrowLeft, Trash2, Save } from "lucide-react";

// Services
import { AddActionPlan } from "@/services/action-plan";

// Components
import { FilePreviewModal } from "@/components/FilePreviewModal";

// helpers
import { useDocumentByType, DOC_TYPES } from "@/helpers/documentTypePreview";
import { S3_BUCKET_LINK } from "@/components/constants";

// Types
interface TableRowData {
  detailName: string;
  amount: number | string;
}

interface AddActionPlanData {
  companyId: string;
  yearly: number;
  planName: string;
  finPlanDetails: {
    financialType: string;
    detailName: string;
    amount: number;
  }[];
  docFile: string;
  description: string;
}

function formatNumberWithCommas(value: string | number) {
  // Convert to string and handle empty/null values
  const stringValue = String(value || "");

  // Check if negative by parentheses
  const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");

  // Strip parentheses before formatting
  const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

  // Split number into integer and decimal parts
  const [integerPart, decimalPart] = numericValue.split(".");

  // Format integer part with commas
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Combine with decimal part if it exists
  const formattedValue =
    decimalPart !== undefined
      ? `${formattedInteger}.${decimalPart}`
      : formattedInteger;

  // Re-wrap if negative
  return isNegative ? `(${formattedValue})` : formattedValue;
}

function parseParenthesisNumber(strValue: string | number) {
  if (!strValue) return 0;
  const stringValue = String(strValue).replace(/,/g, ""); // remove commas

  const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
  const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

  const parsed = Number(numericValue) || 0;
  return isNegative ? -parsed : parsed;
}

function handleNumericChange({
  e,
  index,
  fieldName,
  data,
  setData,
}: {
  e: React.ChangeEvent<HTMLInputElement>;
  index: number;
  fieldName: "amount";
  data: TableRowData[];
  setData: React.Dispatch<React.SetStateAction<TableRowData[]>>;
}) {
  const userInput = e.target.value;

  // Allow digits, parentheses, and decimal point
  const cleaned = userInput.replace(/[^\d().]/g, "");

  // Handle the case where we have both decimal and parentheses
  if (cleaned.includes(".")) {
    const parts = cleaned.split(".");
    const hasClosingParen = parts[parts.length - 1].endsWith(")");

    // If there's a decimal part, limit it to 2 places
    if (parts.length > 1) {
      let decimalPart = parts[1];
      // Remove closing parenthesis if it exists
      if (hasClosingParen) {
        decimalPart = decimalPart.slice(0, -1);
      }
      // Limit decimal places to 2
      decimalPart = decimalPart.slice(0, 2);
      // Add back closing parenthesis if it existed
      if (hasClosingParen) {
        decimalPart += ")";
      }

      const newValue = `${parts[0]}.${decimalPart}`;
      const newData = [...data];
      newData[index][fieldName] = newValue;
      setData(newData);
      return;
    }
  }

  // If no decimal point, just update as is
  const newData = [...data];
  newData[index][fieldName] = cleaned;
  setData(newData);
}

export const Route = createFileRoute(
  "/_layout-private/report/action-plan/addnew"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const companyId = JSON.parse(localStorage.getItem("companyId") || "");
  const { getDocument } = useDocumentByType();
  const doc = getDocument(DOC_TYPES.OPERATION_PLAN);

  const [formData, setFormData] = useState({
    planName: "ແຜນດຳເນີນງານປະຈຳປີ",
    yearly: undefined as number | undefined,
    companyId,
    description: "",
  });
  const [incomeRows, setIncomeRows] = useState<TableRowData[]>([
    { detailName: "ລາຍຮັບຈາກການບໍລິການ", amount: 0 },
    { detailName: "ລາຍຮັບການເງິນ", amount: 0 },
  ]);
  const [expenseRows, setExpenseRows] = useState<TableRowData[]>([
    { detailName: "ວັດຖຸ-ນ້ຳມັນ", amount: 0 },
    { detailName: "ຊື້ ແລະ ບໍລິການຕ່າງໆທາງນອກ", amount: 0 },
    { detailName: "ເງິນເດືອນ ແລະ ລາຍຈ່າຍອື່ນໆ", amount: 0 },
    { detailName: "ຄ່າຫຼຸ້ຍຫ້ຽນໃນທຸລະກິດ", amount: 0 },
    { detailName: "ລາຍຈ່າຍການເງິນ ແລະ ບັງເອີນ", amount: 0 },
  ]);
  const [profitRow, setProfitRow] = useState<TableRowData[]>([
    { detailName: "IV ການຫັກອາກອນ (%)", amount: 0 },
    { detailName: "V ຜົນໄດ້ຮັບຫຼັກຫັກອາກອນ", amount: 0 },
    { detailName: "VI. ຜົນໄດ້ຮັບ (ກຳໄລ)", amount: 0 },
  ]);

  const [fileName, setFileName] = useState<string>("");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const addIncomeRow = () => {
    setIncomeRows([...incomeRows, { detailName: "", amount: 0 }]);
  };

  const addExpenseRow = () => {
    setExpenseRows([...expenseRows, { detailName: "", amount: 0 }]);
  };

  const removeIncomeRow = (index: number) => {
    const newIncomeRows = [...incomeRows];
    newIncomeRows.splice(index, 1);
    setIncomeRows(newIncomeRows);
  };

  const removeExpenseRow = (index: number) => {
    const newExpenseRows = [...expenseRows];
    newExpenseRows.splice(index, 1);
    setExpenseRows(newExpenseRows);
  };

  // React Query Mutation for form submission
  const mutation = useMutation({
    mutationFn: async (data: AddActionPlanData) => {
      await AddActionPlan(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["actionPlan"] });
      toast({
        title: "ເພີ່ມສຳເລັດ",
        description: "ເພີ່ມແຜນດຳເນີນງານສຳເລັດແລ້ວ",
        variant: "success",
      });
      navigate({
        to: "/report/action-plan",
        search: { page: 1, startYear: 0, endYear: 0 },
      });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      if (error.response?.data?.message === "FIN_PLAN_ALREADY_EXIST") {
        toast({
          title: "ເພີ່ມບໍ່ສຳເລັດ",
          description: "ມີແຜນດຳເນີນງານນີ້ມີໃນລະບົບແລ້ວ",
          variant: "destructive",
        });
      } else {
        toast({
          title: "ເພີ່ມບໍ່ສຳເລັດ",
          description: "ກະລຸນາກວດສອບຂໍ້ມູນແລ້ວລອງອີກຄັ້ງ.",
          variant: "destructive",
        });
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!fileName) {
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ກະລຸນາອັບໂຫຼດເອກະສານກ່ອນບັນທຶກ",
        variant: "destructive",
      });

      return;
    }

    if (!formData.yearly) {
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ກະລຸນາເລືອກປີກ່ອນບັນທຶກ",
        variant: "destructive",
      });
      return;
    }

    const companyId = JSON.parse(localStorage.getItem("companyId") || "");
    const data = {
      companyId: companyId || "",
      yearly: formData.yearly,
      planName: formData.planName,
      finPlanDetails: [
        ...expenseRows.map((detail) => ({
          financialType: "EXPENSE",
          detailName: detail.detailName,
          amount: parseParenthesisNumber(detail.amount),
        })),
        ...incomeRows.map((detail) => ({
          financialType: "INCOME",
          detailName: detail.detailName,
          amount: parseParenthesisNumber(detail.amount),
        })),
        ...profitRow.map((detail) => ({
          financialType: "PROFIT",
          detailName: detail.detailName,
          amount: parseParenthesisNumber(detail.amount),
        })),
      ],
      docFile: fileName,
      description: formData.description,
    };
    mutation.mutate(data);
  };

  // ... rest of the code ...

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink>
              <Link
                replace
                to="/report/action-plan"
                search={{ page: 1, startYear: 0, endYear: 0 }}
              >
                ລາຍງານແຜນດຳເນີນງານ
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>ສ້າງລາຍງານແຜນດຳເນີນງານ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <Card className="mt-8">
        <CardHeader>
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/action-plan",
                search: { page: 1, startYear: 0, endYear: 0 },
              })
            }
            className="flex items-center gap-1 mb-4"
            aria-label="Go back to tax obligations report"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>ການສ້າງແຜນດຳເນີນງານ</CardTitle>
              <CardDescription>ໃບລາຍງານແຜນດຳເນີນງານ</CardDescription>
            </div>
            <FilePreviewModal
              fileUrl={`${S3_BUCKET_LINK}${doc?.documentFile}`}
              fileName={doc?.documentName ?? ""}
              text={doc?.documentName ?? ""}
              description={doc?.description}
              showDownloadButton={true}
              useGoogleViewer={true}
            />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            {/* Plan Name and Year Input */}
            <div className="flex flex-col gap-2 mb-4">
              <Label htmlFor="planName">
                ຊື່ເອກະສານ <span className="text-red-500">*</span>
              </Label>
              <Input
                id="planName"
                name="planName"
                type="text"
                onChange={(e) => {
                  setFormData({ ...formData, planName: e.target.value });
                }}
                value={formData.planName}
                defaultValue={"ແຜນດຳເນີນງານປະຈຳປີ"}
                className="w-[200px]"
                required
                placeholder="ກະລຸນາປ້ອນຊື່ແຜນ..."
                disabled
              />
            </div>

            <div className="flex flex-col gap-2 mb-4">
              <Label htmlFor="yearly">
                ປີ <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.yearly?.toString() ?? ""}
                onValueChange={(value) => {
                  setFormData({ ...formData, yearly: Number(value) });
                }}
                required
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="ກະລຸນາເລືອກປີ" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from(
                    { length: new Date().getFullYear() - 1974 },
                    (_, i) => (
                      <SelectItem
                        key={i}
                        value={(new Date().getFullYear() - i).toString()}
                      >
                        {new Date().getFullYear() - i}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Income and Expense Tables */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="py-5 text-center text-black bg-gray-100 border border-gray-300">
                    ເນື້ອໃນ
                  </TableHead>
                  <TableHead className="py-5 text-center text-black bg-gray-100 border border-gray-300 rounded-tr-lg">
                    ແຜນການ
                  </TableHead>
                  <TableHead className="py-5 text-center text-black bg-gray-100 border border-gray-300 rounded-tr-lg">
                    ຈັດການ
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Income Table */}
                <TableRow className="bg-[#216C4C33]">
                  <TableCell colSpan={3} className="font-bold">
                    I. ລາຍຮັບ
                  </TableCell>
                </TableRow>
                {incomeRows.map((_, index) => (
                  <TableRow key={index} className="cursor-pointer">
                    <TableCell className="border">
                      <Input
                        id={`incomeDetails.${index}.detailName`}
                        name={`incomeDetails.${index}.detailName`}
                        placeholder="1,000,000"
                        value={incomeRows[index]?.detailName || ""}
                        onChange={(e) => {
                          const newIncomeRows = [...incomeRows];
                          newIncomeRows[index].detailName = e.target.value;
                          setIncomeRows(newIncomeRows);
                        }}
                        readOnly={index < 2}
                      />
                    </TableCell>
                    <TableCell className="border">
                      <Input
                        id={`incomeDetails.${index}.amount`}
                        name={`incomeDetails.${index}.amount`}
                        placeholder="1,000,000"
                        value={formatNumberWithCommas(
                          incomeRows[index]?.amount
                        )}
                        onChange={(e) => {
                          // const newIncomeRows = [...incomeRows];
                          // newIncomeRows[index].amount = Number(e.target.value);
                          // setIncomeRows(newIncomeRows);

                          handleNumericChange({
                            e,
                            index,
                            fieldName: `amount`,
                            data: incomeRows,
                            setData: setIncomeRows,
                          });
                        }}
                        className="text-right"
                      />
                    </TableCell>
                    <TableCell className="border">
                      {index > 1 ? (
                        <Trash2
                          className="text-red-500"
                          onClick={() => removeIncomeRow(index)}
                        />
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Add Income Row */}
                <TableRow className="cursor-pointer">
                  <TableCell className="border" colSpan={3}>
                    <Button type="button" onClick={addIncomeRow}>
                      <CirclePlus className="mr-2" />
                      ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                    </Button>
                  </TableCell>
                </TableRow>

                {/* Expense Table */}
                <TableRow className="bg-[#216C4C33]">
                  <TableCell colSpan={3} className="font-bold">
                    II. ລາຍຈ່າຍ
                  </TableCell>
                </TableRow>
                {expenseRows.map((_, index) => (
                  <TableRow key={index} className="cursor-pointer">
                    <TableCell className="border">
                      <Input
                        id={`expenseDetails.${index}.detailName`}
                        name={`expenseDetails.${index}.detailName`}
                        placeholder="1,000,000"
                        value={expenseRows[index]?.detailName || ""}
                        onChange={(e) => {
                          const newExpenseRows = [...expenseRows];
                          newExpenseRows[index].detailName = e.target.value;
                          setExpenseRows(newExpenseRows);
                        }}
                        readOnly={index < 5}
                      />
                    </TableCell>
                    <TableCell className="border">
                      <Input
                        id={`expenseDetails.${index}.amount`}
                        name={`expenseDetails.${index}.amount`}
                        placeholder="1,000,000"
                        value={formatNumberWithCommas(
                          expenseRows[index]?.amount
                        )}
                        onChange={(e) => {
                          // const newExpenseRows = [...expenseRows];
                          // newExpenseRows[index].amount = Number(e.target.value);
                          // setExpenseRows(newExpenseRows);

                          handleNumericChange({
                            e,
                            index,
                            fieldName: `amount`,
                            data: expenseRows,
                            setData: setExpenseRows,
                          });
                        }}
                        className="text-right"
                      />
                    </TableCell>
                    <TableCell className="border">
                      {index > 4 ? (
                        <Trash2
                          className="text-red-500"
                          onClick={() => removeExpenseRow(index)}
                        />
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Add Expense Row */}
                <TableRow className="cursor-pointer">
                  <TableCell className="border" colSpan={3}>
                    <Button type="button" onClick={addExpenseRow}>
                      <CirclePlus className="mr-2" />
                      ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                    </Button>
                  </TableCell>
                </TableRow>

                <TableRow className="bg-[#216C4C33]">
                  <TableCell className="font-bold">
                    III. ຜົນໄດ້ຮັບກ່ອນອາກອນ
                  </TableCell>
                  <TableCell className="text-right">
                    {formatNumberWithCommas(
                      incomeRows.reduce(
                        (sum, row) => sum + parseParenthesisNumber(row.amount),
                        0
                      ) -
                        expenseRows.reduce(
                          (sum, row) =>
                            sum + parseParenthesisNumber(row.amount),
                          0
                        )
                    )}
                  </TableCell>
                  <TableCell>-</TableCell>
                </TableRow>
                {profitRow.map((_, index) => (
                  <TableRow
                    key={index}
                    className="cursor-pointer bg-[#216C4C33]"
                  >
                    <TableCell className="font-bold border">
                      {profitRow[index]?.detailName || ""}
                    </TableCell>
                    <TableCell className="border">
                      <Input
                        id={`profitDetail.${index}.amount`}
                        name={`profitDetail.${index}.amount`}
                        placeholder="0"
                        value={formatNumberWithCommas(profitRow[index]?.amount)}
                        onChange={(e) => {
                          // const newExpenseRows = [...expenseRows];
                          // newExpenseRows[index].amount = Number(e.target.value);
                          // setExpenseRows(newExpenseRows);

                          handleNumericChange({
                            e,
                            index,
                            fieldName: `amount`,
                            data: profitRow,
                            setData: setProfitRow,
                          });
                        }}
                        className="text-right"
                      />
                    </TableCell>
                    <TableCell>-</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* File Upload */}
            <div className="flex items-center mt-4 gap-x-2">
              <FileUpload
                currentFile={fileName}
                onFileUpload={(value) => setFileName(value)}
              />
            </div>

            {/* Description */}
            <div className="w-full my-4">
              <p>ລາຍລະອຽດເພີ່ມເຕີມ</p>
              <Textarea
                id="description"
                name="description"
                rows={4}
                className="w-full border"
                placeholder="ປ້ອນລາຍລະອຽດເພີ່ມເຕີມ..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end w-full mt-2 gap-x-3">
              <Button
                variant={"outline"}
                type="button"
                onClick={() => {
                  navigate({
                    to: "/report/action-plan",
                    search: { page: 1, startYear: 0, endYear: 0 },
                  });
                }}
              >
                <span>ຍົກເລີກ</span>
              </Button>
              <Button
                disabled={mutation.isPending}
                type="submit"
                className="flex items-center gap-x-2"
              >
                {mutation.isPending ? (
                  <Spinner className="text-white" />
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    ບັນທຶກ
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
}

export default RouteComponent;
